import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import { IMuseApiOptions } from "@server/server";
import { get } from "@util/object";

export interface IApiProps {
    articlesApiHost: string;
    museApiOptions: IMuseApiOptions;
}

interface IApiResult {
    data: any;
    meta?: any;
}

interface IFilterArgs {
    [key: string]: string | number;
}

export type IApi = (
    props: IApiProps
) => {
    [key: string]: {
        [key: string]: {
            [key: string]: any;
        };
    };
};

const filterRequest = async (
    endpoint: string,
    filterBy: IFilterArgs,
    axiosOptions?: AxiosRequestConfig
): Promise<any> => {
    const response = await axios.get(endpoint, {
        params: filterBy,
        ...axiosOptions
    });
    return response;
};

export const getCurrentUser = () => {
    return axios.get("/api/user");
};

const getPosts = async (articlesApiHost: string, filterArgs: IFilterArgs, axiosOptions?: AxiosRequestConfig) => {
    const response = await filterRequest(`${articlesApiHost}/api/posts`, filterArgs, axiosOptions || {});

    return {
        data: get(response, "data.data"),
        meta: response.data.metadata
    };
};

const getOptimizedPosts = async (articlesApiHost: string, filterArgs: IFilterArgs) => {
    const response = await axios.get(`${articlesApiHost}/api/current/${filterArgs.uri}`, {});

    return {
        data: get(response, "data.data"),
        meta: get(response, "data.metadata")
    };
};

const getOptimizedTiles = async (articlesApiHost: string, filterArgs: IFilterArgs) => {
    const response = await axios.get(`${articlesApiHost}${filterArgs.uri}`, {});

    return {
        pagination: get(response, "data.pagination"),
        data: get(response, "data.articles"),
        meta: get(response, "data.metadata")
    };
};

const getPost = async (articlesApiHost: string, filterArgs: IFilterArgs, axiosOptions?: AxiosRequestConfig) => {
    const { data, meta } = await getPosts(articlesApiHost, filterArgs, axiosOptions);
    return { data, meta };
};

const getOptimizedPost = async (articlesApiHost: string, filterArgs: IFilterArgs) => {
    const { data, meta } = await getOptimizedPosts(articlesApiHost, filterArgs);
    return { data, meta };
};

const getOptimizedTile = async (articlesApiHost: string, filterArgs: IFilterArgs) => {
    const { pagination, data, meta } = await getOptimizedTiles(articlesApiHost, filterArgs);
    return { pagination, data, meta };
};

const getMuseApiAuthOptions = (authOptions: IMuseApiOptions) => {
    return authOptions.authEnabled
        ? {
              auth: {
                  username: authOptions.username,
                  password: authOptions.password
              }
          }
        : {};
};

export const Api: IApi = (props: IApiProps) => ({
    optimizedPosts: {
        get: {
            byUri: async (uri: string) => getOptimizedPost(props.articlesApiHost, { uri })
        },
        getAll: {
            byUri: async (uri: string) => getOptimizedTile(props.articlesApiHost, { uri })
        }
    },
    posts: {
        get: {
            byID: async (id: number, axiosOptions?: AxiosRequestConfig): Promise<IApiResult> => {
                const response = await axios.get(`${props.articlesApiHost}/api/post/${id}`, axiosOptions || {});

                return {
                    data: get(response, "data")
                };
            },
            bySlug: async (slug: string, axiosOptions?: AxiosRequestConfig) =>
                getPost(props.articlesApiHost, { slug }, axiosOptions || {}),
            byUri: async (uri: string, axiosOptions?: AxiosRequestConfig) =>
                getPost(props.articlesApiHost, { uri }, axiosOptions || {}),
            byParams: async (params: any, axiosOptions?: AxiosRequestConfig) =>
                getPosts(props.articlesApiHost, params, axiosOptions || {})
        },
        getAll: {
            byParams: async (paramsObjArray: Array<{ params: { [key: string]: string } }>) => {
                let responses: IMetadataObj[];
                await axios
                    .all(
                        paramsObjArray.map((paramObj: { params: { [key: string]: string } }) => {
                            return axios.get(`${props.articlesApiHost}/api/posts`, paramObj);
                        })
                    )
                    .then(async responseArr => {
                        responses = responseArr.map((response: IMetadataObj) => {
                            return {
                                meta: get(response, "data.metadata"),
                                data: get(response, "data.data")
                            };
                        });
                    });

                return responses;
            }
        }
    },
    companies: {
        get: {
            list: async (filterBy: IFilterArgs, axiosOptions?: AxiosRequestConfig): Promise<IApiResult> => {
                const authOptions = getMuseApiAuthOptions(props.museApiOptions);

                const response = await filterRequest(`${props.museApiOptions.host}/api/companies`, filterBy, {
                    headers: {
                        referer: props.museApiOptions.host
                    },
                    ...authOptions,
                    ...axiosOptions
                });
                return {
                    data: response.data.results,
                    meta: {
                        page: response.data.page,
                        page_count: response.data.page_count,
                        items_per_page: response.data.items_per_page,
                        took: response.data.took,
                        timed_out: response.data.timed_out,
                        total: response.data.total
                    }
                };
            },
            byID: async (id: number, axiosOptions?: AxiosRequestConfig): Promise<IApiResult> => {
                const authOptions = getMuseApiAuthOptions(props.museApiOptions);

                const response = await axios.get(`${props.museApiOptions.host}/api/companies/${id}`, {
                    headers: {
                        referer: props.museApiOptions.host
                    },
                    ...authOptions,
                    ...axiosOptions
                });
                return {
                    data: get(response, "data")
                };
            }
        }
    }
});
